import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Subscription, tap } from 'rxjs';
import { ProdLogTask, ProdLogTaskResponse} from 'src/app/model/Product';
import { SolutionInfo } from 'src/app/model/SolutionInfo';
import { ActivityService } from 'src/app/service/activity.service';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { DataService } from 'src/app/service/data.service';
import { EventService } from 'src/app/service/event.service';
import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { Events, WebsocketService } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnType, DataTableColumn, DataTableFilter, DataTableRow } from '../controls/datatable/datatable.component';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { ProductDataType, ProductsComponent } from '../products/products.component';
import { SolutionService } from 'src/app/service/solution.service';

export enum ProductLogPackageField {
  ID = "id",
  NAME = "prefix",
  PRODUCTNAME = "productName",
  PRODUCTVERSION = "productVersion",
  REQUESTBY = "initiatorUserName",
  CREATED = "createdDateTime",
  LASTUPDATED = "updatedDateTime",
  SIZE = "fileSize",
  STATUS = "uiStatus",
  DOWNLOAD = "downloadUrl"
}

@Component({
    selector: 'app-product-component-logs',
    templateUrl: './product-component-logs.component.html',
    styleUrls: ['./product-component-logs.component.css'],
    standalone: false
})
export class ProductComponentLogsComponent extends ProductsComponent implements OnInit {
  title: string = '';
  cols: DataTableColumn[] = Array<DataTableColumn>(
    new DataTableColumn(ProductLogPackageField.NAME, $localize`Name`, ColumnType.STRING, false, "21.5"),
    new DataTableColumn(ProductLogPackageField.PRODUCTNAME, $localize`Product`, ColumnType.STRING, false, "18.5"),
    new DataTableColumn(ProductLogPackageField.PRODUCTVERSION, $localize`Version`, ColumnType.STRING, false, "14"),
    new DataTableColumn(ProductLogPackageField.REQUESTBY, $localize`Requested By`, ColumnType.STRING, false, "12"),
    new DataTableColumn(ProductLogPackageField.LASTUPDATED, $localize`Updated`, ColumnType.DATEFROMNOW, false, "14"),
    new DataTableColumn(ProductLogPackageField.STATUS, $localize`Status`, ColumnType.STATUSEXT, false, "6"),
    new DataTableColumn(ProductLogPackageField.SIZE, $localize`Size`, ColumnType.FILESIZE, false, "8"),
    new DataTableColumn(ProductLogPackageField.DOWNLOAD, $localize`URL`, ColumnType.DOWNLOADLINK, false, "6")
  );
  filters: DataTableFilter[] = [];
  rows: DataTableRow[] = [];
  total: number = 0;
  isInit: boolean = true;
  currEnvId:number;
  solutionInfo: SolutionInfo;
  loading: boolean = true;
  packagingLogs: boolean = true;
  isReload:boolean = false;

  logTaskResponse: ProdLogTaskResponse;
  
  constructor(protected eventService: EventService,
    protected authService:AuthenticationService,
    protected siService:SoftwareInventoryService,
    protected dataService:DataService,
    protected wsService: WebsocketService,
    protected router: Router,
    protected modal: Modal,
    protected activityService: ActivityService,
    public route: ActivatedRoute,
    protected solutionService: SolutionService) {
      super(eventService, authService, siService, dataService, wsService, router, modal, activityService, solutionService);

      this.isLoading = false;
      
      this.dataSubscription = this.dataService.getData().subscribe((data:any) => {
        if(data && data.type == ProductDataType.AgentManifest){
          this.isAgentManifestUpdating = data.isAgentManifestUpdating;
        } else if(data && data.type == ProductDataType.General){
          this.solutionInfo = data.solutionInfo;
          this.isManifestUpdating = this.solutionInfo.manifestStatus.isUpdating;
        }
      });

      this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
        this.solutionInfo = newSolutionInfo;
        this.title = $localize`Product Log Packages`;
        this.dataService.sendData({type: ProductDataType.Title, title: this.title});
        this.dataService.sendData({type: ProductDataType.UnselectAllTree});
        this.getProdLogTasks();
      });

      this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
        if (msg.eventType == Events.ActivityRefresh) {
          if (msg.body !== undefined && msg.body.type == "PACKAGE_PROD_LOGS"){
            this.refreshTask();
          }
        } else if (msg.eventType == Events.ManifestUpdateStarted) {
          this.isManifestUpdating = true;
        } else if (msg.eventType == Events.ManifestUpdateComplete) {
          this.reloadAfterManifestUpdate();
        } else if (msg.eventType == Events.AgentManifestComplete) {
          this.isAgentManifestUpdating = false;
        }
      });
  }

  ngOnInit(): void {
  }

  ngOnDestroy() {
    if (this.solutionInfoSubscription != undefined) {
      this.solutionInfoSubscription.unsubscribe();
    }
    if (this.eventMessageSubscription != undefined) {
      this.eventMessageSubscription.unsubscribe();
    }
    if (this.dataSubscription != undefined) {
      this.dataSubscription.unsubscribe();
    }
  }
  
  getProdLogTasks(): void {
    this.loading = true;
    this.siService.getProdLogTasks(this.solutionInfo.currentEnvironment.id).pipe(
      tap(resp => {
        this.logTaskResponse = resp.body;
        this.total = this.logTaskResponse.count;

        var tmpRows: DataTableRow[] = [];
        this.logTaskResponse.tasks.forEach(task => {
          tmpRows.push(this.setTaskRow(task));
        });

        this.rows = tmpRows;
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe();
  }

  setTaskRow(task: any): DataTableRow{
    let row: DataTableRow;
    task.uiStatus = Utils.getActivityStatus(task.status);
    task.downloadUrl = (task.status == 'COMPLETED') ? `${this.logTaskResponse.downloadURL}/${task.uuid}?type=1`  : "";
    if (task.status == "QUEUED" || task.status == "RUNNING") {
      row = new DataTableRow(task, []);
      row.disableCheckbox = true;
    } else {
      let menus:ActionMenuItem[] = [];
      if(task.status == "ERROR"){
        menus.push(new ActionMenuItem("Error", $localize`View error`, ActionMenuIcon.DETAILS, task, false));
      }
      menus.push(new ActionMenuItem("Delete", $localize`Delete`, ActionMenuIcon.DELETE, task, true));

      if(task.status == "COMPLETED"){
        menus.push(new ActionMenuItem("Upload", $localize`Upload`, ActionMenuIcon.UPLOAD, task, true));
      }
      
      row = new DataTableRow(task, menus);
    }

    return row;
  }

  refreshTask(): void{
    this.siService.getProdLogTasks(this.solutionInfo.currentEnvironment.id).pipe(
      tap(resp => {
        this.logTaskResponse = resp.body;
        this.total = this.logTaskResponse.count;
        this.logTaskResponse.tasks.forEach(task => {
          this.setTask(this.setTaskRow(task));
        });
      })
    ).subscribe();
  }

  setTask(row: DataTableRow): void{
    for(let i = 0; i < this.rows.length; i++){
      if (this.rows[i].data.uuid == row.data.uuid && this.rows[i].data.status != row.data.status){
        this.rows[i] = row;
        break;
      }
    }
  }

  actionMenuClicked(event: ActionMenuEvent): void {
    switch (event.name) {
      case "Delete":
        this.deleteLogs(event.data);
        break;
      case "Upload":
        this.uploadProductLogs(event.data);
        break;
      case "Error":
        const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Error`,
            type: ModalMessageType.Error,
            message: this.getTaskErrorMsg(event.data[0]),
            primaryBtn: $localize`Close`,
            width: '450px'
          },
          id: 'otsc-view-error-log-task-msg'
        }).afterClosed.subscribe(() => {
          sub.unsubscribe();
        });
      break;
    }
  }

  getTaskErrorMsg(task: ProdLogTask):string {
    let errorMsg:string = "";

    switch (task.errorCode) {
      case "VALIDATE_LOGS_PERM_ERROR":
        errorMsg = $localize`Permission error: ${task.errorMessage}`;
        break;
      case "VALIDATE_LOGS_DISKSPACE_ERROR":
        errorMsg = $localize`Disk space error: ${task.errorMessage}`;
        break;
      case "PKG_LOG_ERROR":
      case "MGR_PKG_PROD_LOG_ERROR":
        errorMsg = $localize`Packaging error: ${task.errorMessage}`;
        break;
      case "PKG_UPLOAD_ERROR":
        errorMsg = $localize`Upload error: ${task.errorMessage}`;
        break;
      case "PRODUCT_COMMUNICATION_FAILURE":
        errorMsg = $localize`Product communication error: ${task.errorMessage}`;
        break;
      case "LOG_DOWNLOAD_PKG_FAILURE":
        errorMsg = $localize`Product download error: ${task.errorMessage}`;
        break;
      case "FAILED_INSTANCE_INFO":
        errorMsg = $localize`Product instance error: ${task.errorMessage}`;
        break;
      case "FAILED_PRODUCT_INFO":
        errorMsg = $localize`Product error: ${task.errorMessage}`;
        break;
      case "PLAN_FILE_UPLOAD_ERROR":
        errorMsg = $localize`Plan file upload error: ${task.errorMessage}`;
        break;
      case "PLAN_FILE_DOWNLOAD_ERROR":
        errorMsg = $localize`Plan file dowload error: ${task.errorMessage}`;
        break;
    }

    return errorMsg;
  }

  deleteLogs(tasks: ProdLogTask[]): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: tasks.length > 1 ? $localize`Delete Logs Packages` : $localize`Delete Logs Package`,
          type: ModalMessageType.Question,
          message: tasks.length > 1 ? $localize`Log Packages may contain information that is no longer available on the Systems in your Environment. Click <b>Delete</b> to confirm deleting of these ${tasks.length} Log Packages.` :
            $localize`Log Packages may contain information that is no longer available on the Systems in your Environment. Click <b>Delete</b> to confirm deleting of this Log Package.`,
          primaryBtn: $localize`Delete`,
          secondaryBtn: $localize`Cancel`,
          width: '550px'
        },
        id: 'otsc-delete-log-task-msg'
      }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          let success: boolean = false;
          this.loading = true;
          let taskids: string[] = [];
          tasks.forEach(task =>{
            taskids.push(task.uuid);
          });
          this.siService.deleteTasks(taskids, this.solutionInfo.currentEnvironment.id).pipe(
            tap(resp => {
              success = true;
              this.getProdLogTasks();
            }),
            finalize(() => {
              if (!success) {
                this.loading = false;
              }
            })
          ).subscribe();
        }
        sub.unsubscribe();
      });
  }

  uploadProductLogs(tasks: ProdLogTask[]): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Uploading Product Log Packages`,
          type: ModalMessageType.Question,
          message: $localize`To upload the selected product log packages, you will be redirected to the <b>Settings > Support</b> page. After the log packages are collected, choose the folder named after your support case to upload them to the SFTP site.  Please make sure that System Center Manager has internet access and that you have opened a support case for files over 25 MB or generated an SFTP transfer.`,
          primaryBtn: $localize`Upload`,
          secondaryBtn: $localize`Cancel`,
          width: '550px'
        },
        id: 'otsc-upload-logs-pkg-msg'
      }).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          //filter compelted tasks
          const tasksToShare:ProdLogTask[] = tasks.filter((task) => {
            return task.status == "COMPLETED";
          })
          this.router.navigate(['/settings/support/logs'], { state: { tasks: tasksToShare } });
        }
        sub.unsubscribe();
      });
  }
}